import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Send, Save, Loader2, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { qualityPPMAPI, programsAPI, inventoryAPI } from '@/lib/api';

const prevMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

export const MonthlyEmailCard = () => {
  const [s, setS] = useState(null);
  const [text, setText] = useState('');
  const [month, setMonth] = useState(prevMonth());
  const [busy, setBusy] = useState('');
  const apply = (d) => { setS(d); setText((d.recipients || []).join('\n')); };
  useEffect(() => { qualityPPMAPI.getEmailSettings().then((r) => apply(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy('save');
    try {
      const res = await qualityPPMAPI.setEmailSettings({ recipients: text.split(/[\n,;]/).map((e) => e.trim()).filter(Boolean) });
      apply(res.data); toast.success('Recipients saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); } finally { setBusy(''); }
  };
  const send = async () => {
    setBusy('send');
    try {
      const res = await qualityPPMAPI.sendMonthlyEmail({ month });
      if (res.data.sent) toast.success(`Report for ${month} sent to ${res.data.recipients.length} recipient(s)`);
      else toast.error(res.data.reason || 'Not sent');
      qualityPPMAPI.getEmailSettings().then((r) => apply(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); } finally { setBusy(''); }
  };
  if (!s) return null;
  return (
    <Card className="shadow-none print:hidden" data-testid="ppm-email-card">
      <CardHeader className="pb-1"><CardTitle className="text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-teal-600" />Monthly quality email
        <span className="text-xs font-normal text-muted-foreground">— sent automatically on the 1st at 9 AM IST for the previous month</span></CardTitle></CardHeader>
      <CardContent className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-2">
          <Label className="text-xs">Extra recipients (one per line; all admins are always included)</Label>
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="quality.manager@company.com" data-testid="ppm-email-recipients-input" />
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={save} disabled={!!busy} data-testid="ppm-email-save-btn">
              {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save recipients</Button>
            <span className="text-[11px] text-muted-foreground" data-testid="ppm-email-all-recipients">Goes to: {s.all_recipients.join(', ') || '—'}</span>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Send a report now</Label>
          <div className="flex gap-2">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9" data-testid="ppm-email-month-input" />
            <Button size="sm" className="h-9 bg-teal-600 hover:bg-teal-700" onClick={send} disabled={!!busy || !month} data-testid="ppm-email-send-btn">
              {busy === 'send' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}Send</Button>
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="ppm-email-last-sent">
            {s.last_sent_at ? `Last sent: ${s.last_sent_month} report on ${new Date(s.last_sent_at).toLocaleString()}` : 'Not sent yet'}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export const LinkProgramDialog = ({ item, onClose, onLinked }) => {
  const [programs, setPrograms] = useState([]);
  const [pid, setPid] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (item) programsAPI.list().then((r) => setPrograms(Array.isArray(r.data) ? r.data : r.data.programs || [])).catch(() => toast.error('Failed to load programs'));
    setPid('');
  }, [item]);
  const link = async () => {
    setSaving(true);
    try {
      await inventoryAPI.linkItemToProgram(item.item_id, { program_id: pid, item_type: 'sale', quantity: 0, unit_price: 0 });
      toast.success('Item linked to program'); onLinked();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to link'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ppm-link-program-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4" />Link item to program</DialogTitle>
          <DialogDescription>{item?.item_unique_id} {item?.item_name} — its production and MRB data will roll up under the chosen program.</DialogDescription>
        </DialogHeader>
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger data-testid="ppm-link-program-select"><SelectValue placeholder="Choose a program" /></SelectTrigger>
          <SelectContent>
            {programs.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`ppm-link-program-option-${p.id}`}>{p.program_number} — {p.program_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ppm-link-program-cancel">Cancel</Button>
          <Button onClick={link} disabled={!pid || saving} data-testid="ppm-link-program-confirm">{saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
